import * as L from 'leaflet';
import { InfrastructureTypeEnum } from '@mocca-technologies/model';

// Drone icon path - Angular copies SVGs from @mocca-technologies/style to assets/icons
const droneIconUrl = 'assets/icons/1_drone.svg';
const droneColorIconUrl = 'assets/icons/drone_oldRed_primaryLight.svg';

/**
 * Creates a recolorable Leaflet drone icon using CSS mask (.map-icon from @mocca-technologies/style)
 * @param colorValue     - CSS color for the icon (e.g.: 'var(--ion-color-warning)', '#FF8C00')
 * @param size           - Total size in pixels (default 32)
 * @param borderColor    - CSS color for the border outline. Omit or pass null for no border.
 * @param borderWidth    - Border blur radius in px (default 1). Smaller = thinner halo.
 * @returns L.DivIcon to use in Leaflet markers
 */
export function createDroneIcon(
  colorValue: string = 'var(--ion-color-primary)',
  size: number = 32,
  borderColor?: string | null,
  borderWidth: number = 1,
  iconUrl: string = droneIconUrl,
): L.DivIcon {
  const hasBorder = !!borderColor;
  const borderProps = hasBorder
    ? `--icon-border-color: ${borderColor}; --icon-border-width: ${borderWidth}px;`
    : '';

  return L.divIcon({
    html: `
      <span class="map-icon${hasBorder ? ' map-icon--bordered' : ''}" style="
        --icon-src: url('${iconUrl}');
        --icon-color: ${colorValue};
        --icon-size: ${size}px;
        ${borderProps}
      "></span>
    `,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}

export function createColoredDroneIcon(
  size: number = 32,
  borderColor?: string | null,
  borderWidth: number = 1,
  iconUrl: string = droneColorIconUrl
): L.DivIcon {
  const hasBorder = !!borderColor;
  return L.divIcon({
    html: `
      <span class="map-icon-colored${hasBorder ? ' map-icon-colored--bordered' : ''}" style="
        --icon-src: url('${iconUrl}');
        --icon-size: ${size}px;
        ${hasBorder ? `--icon-border-color: ${borderColor}; --icon-border-width: ${borderWidth}px;` : ''}
      "></span>
    `,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}

/**
 * Updates the color (and optionally the border) of an already placed drone marker
 * @param marker         - Leaflet marker to update
 * @param colorValue     - New CSS color for the icon
 * @param borderColor    - New border color. Pass null to remove border, omit to leave unchanged.
 * @param borderWidth    - New border width in px (only applied if borderColor is provided)
 */
export function updateDroneIconColor(
  marker: L.Marker,
  colorValue: string,
  borderColor?: string | null,
  borderWidth?: number
): void {
  const element = marker.getElement();
  if (!element) return;

  const iconElement = element.querySelector('.map-icon') as HTMLElement | null;
  if (!iconElement) return;

  iconElement.style.setProperty('--icon-color', colorValue);

  if (borderColor === null) {
    // Quitar reborde
    iconElement.classList.remove('map-icon--bordered');
    iconElement.style.removeProperty('--icon-border-color');
    iconElement.style.removeProperty('--icon-border-width');
  } else if (borderColor !== undefined) {
    // Añadir/actualizar reborde
    iconElement.classList.add('map-icon--bordered');
    iconElement.style.setProperty('--icon-border-color', borderColor);
    if (borderWidth !== undefined) {
      iconElement.style.setProperty('--icon-border-width', `${borderWidth}px`);
    }
  }
  // borderColor omitido → no se toca el reborde existente
}

/**
 * Given an infrastructure type and a manual coordination, returns the corresponding icon
 * To use directly in a leaflet map
 * @param infraType
 * @param manualCoordination
 * @returns
 */
export function getInfrastructureIcon(infraType: InfrastructureTypeEnum, manualCoordination: boolean) {
  switch (infraType) {
    case InfrastructureTypeEnum.INFRST_AIRPORT:
      return manualCoordination ? AirportIconNoAccord : AirportIcon;
    case InfrastructureTypeEnum.INFRST_HELIPORT:
      return manualCoordination ? HeliportIconNoAccord : HeliportIcon;
    case InfrastructureTypeEnum.INFRST_AERODROME:
      return manualCoordination ? AerodromeIconNoAccord : AerodromeIcon;
    case InfrastructureTypeEnum.INFRST_AIRFIELD:
      return manualCoordination ? AirfieldIconNoAccord : AirfieldIcon;
    case InfrastructureTypeEnum.INFRST_NAT_PARK:
      return manualCoordination ? ParcsNaturalsIconNoAccord : ParcsNaturalsIcon;
    case InfrastructureTypeEnum.INFRST_HARBOUR:
      return manualCoordination ? HarbourIconNoAccord : HarbourIcon;
    case InfrastructureTypeEnum.INFRST_NET_TRACK:
      return manualCoordination ? RailWayIconNoAccord : RailWayIcon;
    case InfrastructureTypeEnum.INFRST_UNKNOWN:
    default:
      return UnknownIcon;
  }
}

/**
 * Given an infrastructure type and a manual coordination, returns the corresponding icon URL
 * To use in a popup or a tooltip
 * @param infraType
 * @param manualCoordination
 * @returns
 */
export function getInfrastructureIconURL(infraType: InfrastructureTypeEnum, manualCoordination: boolean) {
  switch (infraType) {
    case InfrastructureTypeEnum.INFRST_AIRPORT:
      return manualCoordination ? AirportIconNoAccord.options.iconUrl : AirportIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_HELIPORT:
      return manualCoordination ? HeliportIconNoAccord.options.iconUrl : HeliportIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_AERODROME:
      return manualCoordination ? AerodromeIconNoAccord.options.iconUrl : AerodromeIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_AIRFIELD:
      return manualCoordination ? AirfieldIconNoAccord.options.iconUrl : AirfieldIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_NAT_PARK:
      return manualCoordination ? ParcsNaturalsIconNoAccord.options.iconUrl : ParcsNaturalsIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_HARBOUR:
      return manualCoordination ? HarbourIconNoAccord.options.iconUrl : HarbourIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_NET_TRACK:
      return manualCoordination ? RailWayIconNoAccord.options.iconUrl : RailWayIcon.options.iconUrl;
    case InfrastructureTypeEnum.INFRST_UNKNOWN:
    default:
      return UnknownIcon.options.iconUrl;
  }
}

/**
 * Returns the icon size based on the window width
 * @returns
 */
function getIconSize(): [number, number] {
  if (window.innerWidth <= 599) {
    return [25, 25];
  } else if (window.innerWidth <= 1199) {
    return [30, 30];
  } else {
    return [40, 40];
  }
}

export const USpaceIcon = {
  iconUrl: 'assets/U-Space-logo.png'
};

export const droneMoccaIcon = {
  iconUrl: 'assets/drone-icon-bg.png'
};

export const conflictIcon = L.icon({
  iconUrl: 'assets/conflict.png',
  iconSize: [40, 40],
});

export const takeOffIcon = L.icon({
  iconUrl: 'assets/Takeoff.png',
  iconSize: [30, 30]
});

export const positionIcon = L.icon({
  iconUrl: 'assets/PositionMarker.png',
  iconSize: [30, 30]
});

export const landingIcon = L.icon({
  iconUrl: 'assets/Landing.png',
  iconSize: [30, 30]
});

export const centroidIcon = L.icon({
  iconUrl: 'assets/icons8.png',
  iconSize: [30, 30],
})

export const coordinationCenter = L.icon({
  iconUrl: 'assets/PositionMarker.png',
  iconSize: [20, 20],
});

export const helipadIcon = L.icon({
  iconUrl: 'assets/blue-helicopter-pad.png',
  iconSize: [30, 30],
});

export const droneIcon = {
  iconUrl: 'assets/EosIconsDrone.svg',
};

export const blueWaypoint = L.icon({
  iconUrl: 'assets/blue-marker.png',
  iconSize: [20, 20],
});

export const blueBigWaypoint = L.icon({
  iconUrl: 'assets/blue-marker.png',
  iconSize: [25, 25],
});

export const blueLand = L.icon({
  iconUrl: 'assets/helipad-blue.png',
  iconSize: [20, 20],
});

export const blueBigLand = L.icon({
  iconUrl: 'assets/helipad-blue.png',
  iconSize: [25, 25],
});

export const blueJoin = L.icon({
  iconUrl: 'assets/join-blue.png',
  iconSize: [20, 20],
});

export const blueBigJoin = L.icon({
  iconUrl: 'assets/join-blue.png',
  iconSize: [25, 25],
});

export const blueFork = L.icon({
  iconUrl: 'assets/fork-blue.png',
  iconSize: [20, 20],
});

export const blueBigFork = L.icon({
  iconUrl: 'assets/fork-blue.png',
  iconSize: [25, 25],
});

export const redWaypoint = L.icon({
  iconUrl: 'assets/custom-marker.png',
  iconSize: [20, 20],
});

export const redBigWaypoint = L.icon({
  iconUrl: 'assets/custom-marker.png',
  iconSize: [25, 25],
});

export const redTakeoff = L.icon({
  iconUrl: 'assets/helipad-red.png',
  iconSize: [20, 20],
});

export const redBigTakeoff = L.icon({
  iconUrl: 'assets/helipad-red.png',
  iconSize: [25, 25],
});

export const redJoin = L.icon({
  iconUrl: 'assets/join-red.png',
  iconSize: [20, 20],
});

export const redBigJoin = L.icon({
  iconUrl: 'assets/join-red.png',
  iconSize: [25, 25],
});

export const redFork = L.icon({
  iconUrl: 'assets/fork-red.png',
  iconSize: [20, 20],
});

export const redBigFork = L.icon({
  iconUrl: 'assets/fork-red.png',
  iconSize: [25, 25],
});

export const greenWaypoint = L.icon({
  iconUrl: 'assets/green-marker.png',
  iconSize: [20, 20],
});

export const greenBigWaypoint = L.icon({
  iconUrl: 'assets/green-marker.png',
  iconSize: [25, 25],
});

export const greenJoin = L.icon({
  iconUrl: 'assets/join-green.png',
  iconSize: [20, 20],
});

export const greenBigJoin = L.icon({
  iconUrl: 'assets/join-green.png',
  iconSize: [25, 25],
});

export const greenFork = L.icon({
  iconUrl: 'assets/fork-green.png',
  iconSize: [20, 20],
});

export const greenBigFork = L.icon({
  iconUrl: 'assets/fork-green.png',
  iconSize: [25, 25],
});

export const AirportIcon = L.icon({
  iconUrl: 'assets/A_circle_outline_Airport.svg',
  iconSize: getIconSize(),
});

export const AerodromeIcon = L.icon({
  iconUrl: 'assets/A_circle_outline_Aerodrome.svg',
  iconSize: getIconSize(),
});

export const AirfieldIcon = L.icon({
  iconUrl: 'assets/A_circle_outline_Airfield.svg',
  iconSize: getIconSize(),
});

export const HeliportIcon = L.icon({
  iconUrl: 'assets/H_circle_outline.svg',
  iconSize: getIconSize(),
});

export const AirportIconNoAccord = L.icon({
  iconUrl: 'assets/A_circle_outline_Airport_NoAccord.svg',
  iconSize: getIconSize(),
});

export const AerodromeIconNoAccord = L.icon({
  iconUrl: 'assets/A_circle_outline_Aerodrome_NoAccord.svg',
  iconSize: getIconSize(),
});

export const AirfieldIconNoAccord = L.icon({
  iconUrl: 'assets/A_circle_outline_Airfield_NoAccord.svg',
  iconSize: [40, 40],
});

export const HeliportIconNoAccord = L.icon({
  iconUrl: 'assets/H_circle_outline_NoAccord.svg',
  iconSize: getIconSize(),
});

// https://www.svgrepo.com/collection/landscapes-12/
export const ParcsNaturalsIcon = L.icon({
  iconUrl: 'assets/ParcsNaturalsIcon.svg',
  iconSize: getIconSize(),
});

export const ParcsNaturalsIconNoAccord = L.icon({
  iconUrl: 'assets/ParcsNaturalsIconNOaccord.svg',
  iconSize: getIconSize(),
});

export const HarbourIcon = L.icon({
  iconUrl: 'assets/HarbourIcon.svg',
  iconSize: getIconSize(),
});

export const HarbourIconNoAccord = L.icon({
  iconUrl: 'assets/HarbourIconNoAccord.svg',
  iconSize: getIconSize(),
});

// https://www.svgrepo.com/svg/214892/train
export const RailWayIcon = L.icon({
  iconUrl: 'assets/RailWayIcon.svg',
  iconSize: getIconSize(),
});

export const RailWayIconNoAccord = L.icon({
  iconUrl: 'assets/RailWayIconNoAccord.svg',
  iconSize: getIconSize(),
});

export const UnknownIcon = L.icon({
  iconUrl: 'assets/UnknownIcon.svg',
  iconSize: getIconSize(),
});

export const LoAsIcon = {
  iconUrl: 'assets/LoAsIcon.svg',
};

export const AgreementIcon = {
  iconUrl: 'assets/Agreement.svg',
};

export const RemoveFilterIconPNG = {
  iconUrl: 'assets/RemoveFilter.png',
  iconSize: [15, 15],
};

export const RemoveFilterIcon = {
  iconUrl: 'assets/RemoveFilter.svg',
};
